import { AxiosInstance } from "axios";
import { createApi } from "./createApi";

/**
 * Import camera configuration that already exists on the customer's VMS
 * (ENG-2461): ignore zones from motion exclusion regions, privacy masks (as
 * zones), camera names and enabled state.
 *
 * actuate_admin:
 * - GET  customer/{id}/about/ carries `importable_settings` (empty when the
 *   integration has nothing to offer) and `integration_label`. Nothing in the
 *   UI renders unless `importable_settings` is non-empty, so an admin without
 *   the feature leaves the UI byte-identical.
 * - GET  customer/{id}/import_settings/preview/?camera_ids=1,2&settings=zones,…
 *   → per-camera source values (`null` = the source has nothing).
 * - POST customer/{id}/import_settings/ {camera_ids, settings, overwrite}
 *   → one result row per camera × setting.
 *
 * Polygons are `[x, y]` fractions of the frame, origin top-left — the same
 * shape the ignore-zone editor saves.
 */

export type ImportSettingKey = "zones" | "masks" | "names" | "enabled";

export const IMPORT_SETTING_KEYS: ImportSettingKey[] = [
  "zones",
  "masks",
  "names",
  "enabled",
];

export type ImportPolygon = number[][];

export interface ImportZonesValue {
  polygons: ImportPolygon[];
  source: string;
  grid?: string;
  has_existing: boolean;
}

export interface ImportScalarValue<T> {
  value: T;
  current: T | null;
  has_existing: boolean;
}

export interface ImportPreviewCamera {
  id: number;
  name: string;
  zones?: ImportZonesValue | null;
  masks?: ImportZonesValue | null;
  names?: ImportScalarValue<string> | null;
  enabled?: ImportScalarValue<boolean> | null;
}

export interface ImportPreviewError {
  camera_id: number | null;
  setting: string;
  message: string;
}

export interface ImportSettingsPreview {
  integration: string;
  integration_label: string;
  importable_settings: string[];
  cameras: ImportPreviewCamera[];
  schedules?: unknown[];
  errors?: ImportPreviewError[];
}

export type ImportResultStatus =
  | "imported"
  | "skipped_has_value"
  | "no_source_value"
  | "error";

export interface ImportSettingsResult {
  camera_id: number;
  setting: string;
  status: ImportResultStatus;
  message?: string;
}

export interface ImportSettingsApplyResponse {
  results: ImportSettingsResult[];
  schedule?: unknown;
}

/** The two About fields this feature reads. */
export interface SiteImportCapabilities {
  importable_settings: string[];
  integration_label: string;
}

const api = (): AxiosInstance => createApi(``);

export const EMPTY_IMPORT_CAPABILITIES: SiteImportCapabilities = {
  importable_settings: [],
  integration_label: "",
};

/** Normalises the two About fields; older admin builds send neither. */
export const readImportCapabilities = (
  about: Partial<SiteImportCapabilities> | null | undefined
): SiteImportCapabilities => {
  const settings = about?.importable_settings;
  const label = about?.integration_label;
  return {
    importable_settings: Array.isArray(settings)
      ? settings.filter((s) => typeof s === "string")
      : [],
    integration_label: typeof label === "string" ? label : "",
  };
};

export const fetchSiteImportCapabilities = async (
  siteId: string | number,
  signal?: AbortSignal
): Promise<SiteImportCapabilities> => {
  const response = await api().get<Partial<SiteImportCapabilities>>(
    `customer/${siteId}/about/`,
    { signal }
  );
  return readImportCapabilities(response.data);
};

export const buildImportPreviewUrl = (
  siteId: string | number,
  cameraIds?: number[],
  settings?: string[]
): string => {
  const query: string[] = [];
  if (cameraIds && cameraIds.length > 0) {
    query.push(`camera_ids=${cameraIds.join(",")}`);
  }
  if (settings && settings.length > 0) {
    query.push(`settings=${settings.join(",")}`);
  }
  const base = `customer/${siteId}/import_settings/preview/`;
  return query.length > 0 ? `${base}?${query.join("&")}` : base;
};

/** `cameraIds` omitted = every camera on the site; `settings` omitted = all. */
export const previewImportSettings = async (
  siteId: string | number,
  cameraIds?: number[],
  settings?: string[],
  signal?: AbortSignal
): Promise<ImportSettingsPreview> => {
  const response = await api().get<ImportSettingsPreview>(
    buildImportPreviewUrl(siteId, cameraIds, settings),
    { signal }
  );
  return response.data;
};

export const applyImportSettings = async (
  siteId: string | number,
  cameraIds: number[],
  settings: string[],
  overwrite: boolean
): Promise<ImportSettingsApplyResponse> => {
  const response = await api().post<ImportSettingsApplyResponse>(
    `customer/${siteId}/import_settings/`,
    { camera_ids: cameraIds, settings, overwrite }
  );
  return response.data;
};

// ---------------------------------------------------------------------------
// Pure helpers for the dialog copy

const hasPolygons = (value: ImportZonesValue | null | undefined): boolean =>
  Boolean(value && Array.isArray(value.polygons) && value.polygons.length > 0);

export interface ImportPreviewCounts {
  total: number;
  /** Cameras with at least one motion-exclusion polygon. */
  zones: number;
  /** Cameras with at least one privacy mask. */
  masks: number;
  /** Cameras the source has a name for. */
  names: number;
  /** …of which the source name differs from the current name. */
  namesDiffer: number;
  /** Cameras the source reports an enabled state for. */
  enabled: number;
  /** …of which the source has disabled. */
  disabled: number;
}

export const countImportPreview = (
  preview: ImportSettingsPreview | null | undefined
): ImportPreviewCounts => {
  const counts: ImportPreviewCounts = {
    total: 0,
    zones: 0,
    masks: 0,
    names: 0,
    namesDiffer: 0,
    enabled: 0,
    disabled: 0,
  };
  const cameras = preview?.cameras ?? [];
  counts.total = cameras.length;
  cameras.forEach((camera) => {
    if (hasPolygons(camera.zones)) counts.zones += 1;
    if (hasPolygons(camera.masks)) counts.masks += 1;
    if (camera.names) {
      counts.names += 1;
      if (camera.names.value !== camera.names.current) counts.namesDiffer += 1;
    }
    if (camera.enabled) {
      counts.enabled += 1;
      if (camera.enabled.value === false) counts.disabled += 1;
    }
  });
  return counts;
};

/**
 * True when at least one camera carries a non-null value for one of the
 * offered setting groups — the gate for showing the onboarding card at all.
 */
export const previewHasImportableValues = (
  preview: ImportSettingsPreview | null | undefined,
  offered: string[]
): boolean => {
  const cameras = preview?.cameras ?? [];
  const offer = new Set(offered);
  return cameras.some(
    (camera) =>
      (offer.has("zones") && hasPolygons(camera.zones)) ||
      (offer.has("masks") && hasPolygons(camera.masks)) ||
      (offer.has("names") && camera.names != null) ||
      (offer.has("enabled") && camera.enabled != null)
  );
};

export interface ImportResultSummary {
  imported: number;
  skipped_has_value: number;
  no_source_value: number;
  error: number;
  errors: ImportSettingsResult[];
}

export const summarizeImportResults = (
  results: ImportSettingsResult[] | null | undefined
): ImportResultSummary => {
  const summary: ImportResultSummary = {
    imported: 0,
    skipped_has_value: 0,
    no_source_value: 0,
    error: 0,
    errors: [],
  };
  (results ?? []).forEach((row) => {
    switch (row.status) {
      case "imported":
        summary.imported += 1;
        break;
      case "skipped_has_value":
        summary.skipped_has_value += 1;
        break;
      case "no_source_value":
        summary.no_source_value += 1;
        break;
      default:
        summary.error += 1;
        summary.errors.push(row);
    }
  });
  return summary;
};
